import React, { useState, useRef, useEffect } from "react";

interface DraggableImageProps {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  onDrag: (id: string, x: number, y: number) => void;
  onDragEnd?: (id: string, x: number, y: number) => void;
  onResize: (id: string, width: number, height: number) => void;
  onResizeEnd?: (id: string, width: number, height: number) => void;
  onDelete: (id: string) => void;
}

const DraggableImage: React.FC<DraggableImageProps> = ({
  id,
  url,
  x,
  y,
  width,
  height,
  onDrag,
  onDragEnd,
  onResize,
  onResizeEnd,
  onDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [startDimensions, setStartDimensions] = useState({ width, height });
  const imageRef = useRef<HTMLDivElement>(null);

  // 处理拖动开始
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // 只响应左键点击
    e.stopPropagation();
    setIsDragging(true);
    setDragStartPos({ x: e.clientX - x, y: e.clientY - y });
  };

  // 处理调整大小开始
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStartPos({ x: e.clientX, y: e.clientY });
    setStartDimensions({ width, height });
  };

  // 处理鼠标移动（拖动或调整大小）
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStartPos.x;
        const newY = e.clientY - dragStartPos.y;
        onDrag(id, newX, newY);
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStartPos.x;
        const deltaY = e.clientY - resizeStartPos.y;
        const aspectRatio = startDimensions.width / startDimensions.height;
        
        // 保持纵横比的调整
        let newWidth = Math.max(50, startDimensions.width + deltaX);
        let newHeight = newWidth / aspectRatio;
        
        onResize(id, newWidth, newHeight);
      }
    };

    const handleMouseUp = () => {
      // 在鼠标释放时调用相应的结束回调
      if (isDragging && onDragEnd) {
        onDragEnd(id, x, y);
      } else if (isResizing && onResizeEnd) {
        onResizeEnd(id, width, height);
      }
      
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [id, isDragging, isResizing, dragStartPos, resizeStartPos, startDimensions, onDrag, onResize, onDragEnd, onResizeEnd, x, y, width, height]);

  return (
    <div
      ref={imageRef}
      className="draggable-image"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        position: "absolute",
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
    >
      <img
        src={url}
        alt="用户上传图片"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
      <div
        className="resize-handle bottom-right"
        onMouseDown={handleResizeStart}
      />
      <button
        className="delete-image-btn"
        onClick={() => onDelete(id)}
        title="删除图片"
      >
        ×
      </button>
    </div>
  );
};

export default DraggableImage; 